import type { ResultStatusType } from 'antd/es/result';

import { Result, Spin } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';

import CopyButton from 'src/components/CopyButton';
import FeedbackForm from 'src/components/FeedbackForm';
import TimerProgress from 'src/components/TimerProgress';
import { useAppDispatch, useAppSelector } from 'src/hooks/store';
import { useEvents } from 'src/hooks/UseEvent';
import useCopyShortcut from 'src/hooks/UseCopyShortcut';
import {
  actions,
  Status,
  saveSummary,
  selectStatusByLabel,
  selectSummaryContent,
  validateSummary,
} from 'src/state/events.state';

import './index.css';

/**
 * AutoSummary component
 */
function AutoSummary() {
  const dispatch = useAppDispatch();
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const copyStatus = useAppSelector(state => selectStatusByLabel(state, 'copy'));
  const saveStatus = useAppSelector(state => selectStatusByLabel(state, 'save'));
  const validateStatus = useAppSelector(state => selectStatusByLabel(state, 'validate'));
  const summaryContentReceived = useAppSelector(selectSummaryContent);

  const { t } = useTranslation();

  const [summaryContent, setSummaryContent] = useState(summaryContentReceived);
  const summaryContentRef = useRef(summaryContent);

  const timeoutParam = searchParams.get('t');
  const timeoutSeconds =
    timeoutParam && !Number.isNaN(Number(timeoutParam)) ? Number(timeoutParam) : null;

  const [remainingTime, setRemainingTime] = useState(timeoutSeconds);
  const [isTimeoutReached, setIsTimeoutReached] = useState(false);

  const loading = !summaryContentReceived;

  const hasActiveTimer =
    timeoutSeconds && remainingTime !== null && remainingTime > 0 && !isTimeoutReached;
  const shouldShowTimer = timeoutSeconds && remainingTime !== null && !loading;
  const isDuringTimer =
    timeoutSeconds !== null && remainingTime !== null && remainingTime > 0 && !isTimeoutReached;

  const saveCallback = useCallback(async () => {
    if (sessionId && summaryContentRef.current !== summaryContentReceived) {
      await dispatch(saveSummary({ content: summaryContentRef.current }));
    }
  }, [sessionId, summaryContentReceived, dispatch]);

  const validateCallback = useCallback(async () => {
    await dispatch(validateSummary());
  }, [dispatch]);

  const handleTimeoutActions = useCallback(async () => {
    await Promise.all([validateCallback(), saveCallback()]);
  }, [validateCallback, saveCallback]);

  const handleOnCopyButtonClick = async () => {
    if (isTimeoutReached) {
      await handleTimeoutActions();
    } else if (isDuringTimer) {
      setIsTimeoutReached(true);
      setRemainingTime(0);
      await handleTimeoutActions();
    } else {
      await Promise.all([
        navigator.clipboard.writeText(summaryContent),
        validateCallback(),
        saveCallback(),
      ]);
    }
    dispatch(actions.setStatus({ label: 'copy', status: Status.SUCCESS }));
  };

  useCopyShortcut(async () => {
    await handleOnCopyButtonClick();
  });

  useEvents(sessionId);

  useEffect(() => {
    setSummaryContent(summaryContentReceived);
  }, [summaryContentReceived]);

  useEffect(() => {
    summaryContentRef.current = summaryContent;
  }, [summaryContent]);

  useEffect(() => {
    if (hasActiveTimer && !loading) {
      const timer = setInterval(() => {
        setRemainingTime(prev => {
          if (prev !== null && prev <= 1) {
            setIsTimeoutReached(true);
            handleTimeoutActions().catch(console.error);

            return 0;
          }

          return prev !== null ? prev - 1 : null;
        });
      }, 1000);

      return () => clearInterval(timer);
    }

    return undefined;
  }, [timeoutSeconds, remainingTime, isTimeoutReached, handleTimeoutActions, loading]);

  if (copyStatus === Status.SUCCESS) {
    const resultStatus: ResultStatusType =
      saveStatus === Status.ERROR || validateStatus === Status.ERROR ? 'error' : 'success';

    return (
      <Result
        status={resultStatus}
        title={t(`Autosummary.feedback.${resultStatus}.title`)}
        subTitle={t(`Autosummary.feedback.${resultStatus}.subTitle`)}
      >
        <FeedbackForm />
      </Result>
    );
  }

  return (
    <Spin
      size="large"
      spinning={saveStatus === Status.PENDING || validateStatus === Status.PENDING}
    >
      <div className="AutoSummary p-8 h-full">
        <div className="flex flex-col items-center">
          <Spin
            tip={t`Autosummary.loading.title`}
            size="large"
            spinning={loading}
            wrapperClassName="flex w-full"
          >
            <div className="flex-1 w-full">
              {shouldShowTimer && (
                <TimerProgress
                  remainingTime={remainingTime}
                  totalTime={timeoutSeconds}
                  isTimeoutReached={isTimeoutReached}
                />
              )}
              <TextArea
                className={`flex-1 grow-1 w-full p-4 ${
                  isTimeoutReached ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : ''
                }`}
                value={summaryContent}
                onChange={({ target }) => setSummaryContent(target.value)}
                placeholder={t`Autosummary.content.placeholder`}
                autoSize={{ minRows: 4 }}
                readOnly={isTimeoutReached}
                disabled={isTimeoutReached}
              />
            </div>
          </Spin>
          <CopyButton
            disabled={loading}
            onClick={handleOnCopyButtonClick}
            save={summaryContent !== summaryContentReceived}
            isNextMode={isTimeoutReached}
            isDoneMode={isDuringTimer}
          />
        </div>
      </div>
    </Spin>
  );
}

export default AutoSummary;
